import React from 'react'
import clsx from 'clsx'
import { useRouter } from 'next/router'
import { useForm } from 'react-hook-form'

import { Text } from '@wartek-id/text'
import { Button } from '@wartek-id/button'
import { Textarea } from '@wartek-id/textarea'
import { useToast } from '@wartek-id/toast'

import FeedbackCard from './FeedbackCard'
import { usePostAksiNyataFeedback } from 'api/microlearning/useAPIAksiNyata'

import type { QueryStatus } from 'react-query'
import type { AksiNyataFeedback } from 'types/microlearning'

interface AksiNyataFeedbackProps {
  aksiNyataId: number | string
  feedbacks: AksiNyataFeedback[]
  feedbackFetchStatus: QueryStatus
  topikId: string
  topikName: string
  hasFeedback: boolean
  onShareButtonClick?: () => void
  onGotoFormClick?: () => void
  onViewFeedBack?: () => void
  isOwner?: boolean
  onRefreshFeedback: Function
}

const AksiNyataFeedbackList = ({
  aksiNyataId,
  feedbackFetchStatus,
  feedbacks,
  hasFeedback,
  topikId,
  topikName = '',
  onShareButtonClick = () => {},
  isOwner = false,
  onRefreshFeedback,
}: AksiNyataFeedbackProps) => {
  const toast = useToast()
  const { query } = useRouter()
  const {
    register,
    trigger,
    getValues,
    formState: { errors },
  } = useForm({ mode: 'onChange' })

  const tanggapanPlaceholderText =
    'Contoh: Secara keseluruhan kontennya sangat bagus dan sesuai dengan tuntutan standar kompetensi murid. Namun, sebaiknya diberikan contoh agar materi dapat lebih mudah dipahami.'

  const { mutateAsync, isLoading } = usePostAksiNyataFeedback(
    query.id as string,
    {
      onSuccess: async () => {
        toast({
          message: 'Berhasil kirim tanggapan!',
          type: 'success',
        })
        await onRefreshFeedback()
      },
      onError: () => {
        toast({
          message: 'Mohon maaf, tanggapan gagal dikirim. Silahkan coba lagi',
        })
      },
    }
  )

  const submitForm = async () => {
    const isValidated = await trigger(['tanggapan'])
    if (isValidated) {
      const value = getValues()
      const likerts = {
        likert_1: '1',
        likert_2: '1',
        likert_3: '1',
        likert_4: '1',
        likert_5: '1',
      }
      await mutateAsync({
        submission: {
          ...likerts,
          ...value,
        },
      })
    }
  }

  return (
    <>
      <div
        className={clsx(
          'px-4 mb-4 pt-4 border-t border-subdued',
          hasFeedback && 'pb-5 border-b'
        )}
      >
        <Text className="mb-4" variant="heading-sm">
          Komentar
        </Text>
        {isOwner && (
          <div>
            <Text>
              Ingin tahu hal yang perlu Anda pertahankan/tingkatkan dari Aksi
              Nyata ini? Bagikan ke rekan guru untuk mendapat tanggapan.
            </Text>
            <div className="mt-3">
              <Button onClick={onShareButtonClick} size="lg" fullWidth>
                Bagikan Aksi Nyata
              </Button>
            </div>
          </div>
        )}
        {!isOwner && !hasFeedback && (
          <>
            <div className="mb-4 bg-white rounded-lg">
              <Textarea
                placeholder={tanggapanPlaceholderText}
                isDisabled={isLoading}
                id="tanggapan"
                {...register('tanggapan', {
                  validate: {
                    required: (v) =>
                      v.trim().length > 0 || 'Tanggapan tidak boleh kosong',
                  },
                })}
                isInvalid={Boolean(errors.tanggapan)}
                errorMessage={errors.tanggapan?.message as string}
              />
            </div>

            <div className="mt-4">
              <Button
                onClick={submitForm}
                isLoading={isLoading}
                disabled={isLoading}
                color="blue"
                fullWidth
                size="lg"
              >
                Kirim Komentar
              </Button>
            </div>
          </>
        )}
        {!isOwner && hasFeedback && (
          <div className="p-3 border border-default rounded">
            <Text>
              Terima kasih atas tanggapan Anda! Jika berkenan, bantu bagikan
              juga ke rekan guru lainnya untuk mendapat lebih banyak masukan.
            </Text>
            <div className="mt-3">
              <Button
                color="white"
                onClick={onShareButtonClick}
                size="lg"
                fullWidth
              >
                Bagikan Aksi Nyata
              </Button>
            </div>
          </div>
        )}
      </div>
      <div className="px-4">
        {feedbackFetchStatus === 'success' &&
          feedbacks?.length > 0 &&
          feedbacks?.map((feedback, index) => (
            <FeedbackCard
              key={index}
              aksiNyataId={aksiNyataId}
              topikId={topikId}
              topikName={topikName}
              authorId={feedback.author.id as string}
              authorName={feedback.author.name}
              authorEmail={feedback.author.email}
              createdAt={feedback.createdAt}
              avatarUrl={undefined}
              content={feedback.submission.tanggapan}
            />
          ))}
      </div>
    </>
  )
}

export default AksiNyataFeedbackList
